import { useQuery } from '@apollo/client';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { OutcropFieldPicturesPageQuery } from '~/apollo/generated/v4/graphql';
import { Modal } from '~/components/common/Modal';
import { NoItemsAlert } from '~/components/common/NoItemsAlert';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useModalState } from '~/hooks/modal';
import { useRouteParam } from '~/hooks/routing';

const OUTCROP_FIELD_PICTURES_PAGE = graphql(`
  query OutcropFieldPicturesPage($id: ID!) {
    outcropGet(id: $id) {
      id
      fieldPictures {
        ...fieldPictureParts
        pictureUrl
        thumbnailUrl: pictureUrl(width: 300)
        file {
          ...fileParts
        }
      }
    }
  }
`);

export default function OutcropFieldPicturesPage() {
  return (
    <ApolloProviderV4>
      <OutcropFieldPicturesPageInner />
    </ApolloProviderV4>
  );
}

function OutcropFieldPicturesPageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_FIELD_PICTURES_PAGE, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;
  const fieldPics = outcrop?.fieldPictures ?? [];

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  if (!fieldPics.length) return <NoItemsAlert show entity="field pictures" />;

  return (
    <>
      <div className="space-y-6">
        <div className="grid md:grid-cols-4 gap-6">
          {fieldPics.map(picture => (
            <FieldPictureModal key={picture.id} fieldPicture={picture} />
          ))}
        </div>
      </div>
    </>
  );
}

type Outcrop = NonNullable<OutcropFieldPicturesPageQuery['outcropGet']>;
type FieldPic = Outcrop['fieldPictures'][number];

function FieldPictureModal({ fieldPicture }: { fieldPicture: FieldPic }) {
  const { show, showModal, hideModal } = useModalState();

  return (
    <>
      <div>
        <button
          type="button"
          className="w-full aspect-square bg-cover border border-slate-200 hover:border-primary cursor-pointer bg-center"
          style={{ backgroundImage: `url("${fieldPicture.thumbnailUrl}")` }}
          onClick={showModal}
        />
      </div>

      <Modal open={show} onHide={hideModal} closeable size="lg">
        <Modal.Body title={fieldPicture.description || 'Field Picture Detail'}>
          <div className="space-y-4 text-center">
            <img
              src={fieldPicture.pictureUrl}
              className="w-full h-auto"
              alt={fieldPicture.description || 'Field picture'}
            />

            <div className="space-y-1">
              {fieldPicture.description && <p>{fieldPicture.description}</p>}
              {fieldPicture.author && (
                <div>
                  <span className="font-bold">Author:</span>{' '}
                  {fieldPicture.author}
                </div>
              )}
            </div>

            {fieldPicture.location && (
              <>
                <hr />
                <GoogleMap
                  center={{
                    lat: fieldPicture.location.latitude,
                    lng: fieldPicture.location.longitude,
                  }}
                  zoom={10}
                  mapContainerClassName="w-full h-60 rounded-md mx-auto"
                  options={{
                    fullscreenControl: false,
                    zoomControl: false,
                    mapTypeId: google.maps.MapTypeId.TERRAIN,
                  }}
                >
                  <MarkerF
                    position={{
                      lat: fieldPicture.location.latitude,
                      lng: fieldPicture.location.longitude,
                    }}
                  />
                </GoogleMap>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
